import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tooltip, tooltipClasses, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import "./marker.css";
import { useNavigate } from 'react-router-dom';
import { equipmentISOStatus, getEquipmentStatusColorByStatus, markerColorByStatus } from 'data/data';
import { getEquipmentLocationMapByMapId } from 'components/axios/equipmentZoneAxios';
import { useEnumApi } from 'contexts/EnumApiContext';
import RightAsideCard from './RightAsideCard';
import bgImg from "assets/images/dashboard_sub_bg.png";
import { ImageMarkerConnectLine } from './ImageMarkerConnectLine';

const TransparentTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    padding: 0,
    maxWidth: 'none',
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: 'rgba(0, 0, 0, 0.75)',
  },
}));
const INTERVAL_TIME = 3000;// 5000ms = 5초 (원하는 시간 간격으로 조정 가능)

export function CompanyImageViewer({ fileUrl, mapsId, zoneId, zoneLocationMap, equipments, latestLogs }) {
  const { getEnumValueByEnumNameAndCode } = useEnumApi();
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width:768px)');
  const navigate = useNavigate();

  const [equipmentMappingList, setEquipmentMappingList] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const containerRef = useRef(null);
  const wrapperRef = useRef(null);
  const [activeMarker, setActiveMarker] = useState(null);
  const [activeArrowColor, setActiveArrowColor] = useState(null);
  const [aspectRatio, setAspectRatio] = useState(null);

  // --- [자동 롤링을 위한 상태 및 Ref] ---
  const [isAutoRolling, setIsAutoRolling] = useState(true); // 자동 롤링 진행 여부
  const [currentIndex, setCurrentIndex] = useState(0);       // 현재 순환 중인 마커 인덱스
  const isAutoRollingRef = useRef(isAutoRolling);
  
  // Ref 값 동기화 (이벤트 핸들러 내 참조용)
  useEffect(() => {
    isAutoRollingRef.current = isAutoRolling;
  }, [isAutoRolling]);

  // 이미지 로드 시 원본 비율 계산
  const handleImageLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.target;
    if (naturalWidth && naturalHeight) {
      setAspectRatio(naturalWidth / naturalHeight);
    }
  };

  // 마커 선택 시 상태 일괄 업데이트 공통 함수
  const applySelectedMarker = (marker) => {
    if (!marker) {
      setActiveMarker(null);
      setSelectedId(null);
      setActiveArrowColor(null);
      return;
    }
    const statusColor = getEquipmentStatusColorByStatus(marker.equipment?.status);
    setActiveMarker(marker);
    setSelectedId(marker.equipment?.id || null);
    setActiveArrowColor(statusColor?.bg2);
  };

  // 마커 클릭 핸들러
  const handleMarkerClick = (marker, index) => {
    const isSame = activeMarker && (activeMarker.id === marker.id || activeMarker.mId === marker.mId);

    if (isSame && !isAutoRolling) {
      // 이미 수동 선택된 마커를 다시 클릭한 경우 -> 자동 롤링 재개 (현재 클릭한 위치부터)
      setCurrentIndex(index);
      setIsAutoRolling(true);
    } else {
      // 다른 마커 클릭 또는 롤링 중에 마커 클릭 -> 롤링 멈추고 해당 마커 고정
      setIsAutoRolling(false);
      setCurrentIndex(index);
      applySelectedMarker(marker);
    }
  };

  // 빈 바탕 클릭 시 자동 롤링 재개
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        !e.target.closest('.marker-element') &&
        !e.target.closest('.MuiTooltip-popper') &&
        !e.target.closest('.right-aside-card')
      ) {
        // 수동 선택 상태에서 바탕을 누르면 롤링 재개
        if (!isAutoRollingRef.current) {
          setIsAutoRolling(true);
        }
      }
    };

    window.addEventListener('click', handleOutsideClick);
    return () => window.removeEventListener('click', handleOutsideClick);
  }, []);

  // API 데이터 로드
  useEffect(() => {
    getEquipmentLocationMapByMapId(mapsId).then((res) => {
      if (res) {
        let mappingList = [];
        res.coordinates?.forEach((m) => {
          let markerId = `marker-${m.equipment?.id}`;
          mappingList.push({ ...m, mId: markerId });
        });

        let markers = [];
        mappingList?.forEach((m) => {
          const data = equipments.find((e) => e.id === m.equipment?.id);
          markers.push({
            ...m,
            equipment: data ? { ...data } : null,
          });
        });

        setEquipmentMappingList(markers);
        setCurrentIndex(0); // 데이터 새로 갱신 시 첫 번째부터 시작
      } else {
        setEquipmentMappingList(null);
      }
    });
  }, [zoneId, mapsId, equipments, latestLogs]);

  // --- [주기적 자동 롤링 타이머 Engine] ---
  useEffect(() => {
    // 마커 데이터가 없거나 수동 정지 상태인 경우 타이머 미작동
    if (!equipmentMappingList || equipmentMappingList.length === 0 || !isAutoRolling) {
      return;
    }

    // 1. 현재 인덱스의 마커를 즉시 활성화
    const targetMarker = equipmentMappingList[currentIndex];
    applySelectedMarker(targetMarker);

    // 2. 일정 시간(3초)마다 다음 인덱스로 이동
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % equipmentMappingList.length;
        const nextMarker = equipmentMappingList[nextIndex];
        applySelectedMarker(nextMarker);
        return nextIndex;
      });
    }, INTERVAL_TIME); 

    return () => clearInterval(interval);
  }, [equipmentMappingList, isAutoRolling, currentIndex]);

  return (
    <React.Fragment>
      <div 
        ref={containerRef}
        className="company-image-viewer"
        style={{ 
          position: "relative", 
          display: "flex", 
          gap: "16px", 
          padding: "10px", 
          color: "#e5e7eb",
          width: "100%" 
        }}
      >
        <div style={{ borderRadius: "12px", padding: "10px", width: "100%", backgroundImage: `url(${bgImg})`, backgroundSize: "cover" }}>
          
          <div className="company-image-panel" style={{ width: "100%", height: "565px", borderRadius: "12px", display: "flex", flexDirection: "column", gap: "10px", overflow: "visible" }}>
            
            <div className="company-image-stage" style={{ width: "100%", flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", overflow: "visible" }}>
            
              <div
                ref={wrapperRef}
                className="company-image-wrapper"
                style={{ 
                  position: "relative", 
                  maxHeight: "100%", 
                  maxWidth: "100%",
                  aspectRatio: aspectRatio ? `${aspectRatio}` : "auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                <img
                  src={fileUrl}
                  alt="image"
                  onLoad={handleImageLoad}
                  className="company-map-image"
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "block",
                    objectFit: "contain",
                  }}
                />

                {equipmentMappingList?.map((m, index) => {
                  const statusColor = getEquipmentStatusColorByStatus(m.equipment?.status);
                  const isoStatus = equipmentISOStatus.find((iso) => iso.code === m.equipment?.lowestDiagnosisRank);
                  const isoColor = isoStatus?.color3 || '#62b929';
                  const isoCode = (isoStatus || { code: "-" }).code;

                  const posX = (m.yawOrX ?? 0) * 100;
                  const posY = (m.pitchOrY ?? 0) * 100;

                  const markerKey = m.mId || `marker-${m.id}`;
                  
                  // 활성화 상태 판별 (자동 롤링 중이든 수동 클릭 중이든 activeMarker 상태에 의존)
                  const isActive = !!activeMarker && (activeMarker.mId === m.mId || activeMarker.id === m.id);

                  // 마커 상태 대표 색상 (기본값 초록)
                  const mainStatusColor = statusColor?.bg2 || '#2672f7';

                  // 상단 35% 이내일 때 bottom 배치
                  const isTopArea = posY < 35;
                  const dynamicPlacement = isTopArea ? "bottom" : "top";

                  return (
                  <React.Fragment key={markerKey}>
                    {/* 1. 활성화(선택)된 마커 뒤에 강조용 아우라 파동 링 바깥쪽에 배치 */}
                    {isActive && (
                      <div
                        className="active-marker-aura"
                        style={{
                          top: `${posY}%`,
                          left: `${posX}%`,
                          backgroundColor: mainStatusColor,
                          boxShadow: `0 0 15px 5px ${mainStatusColor}`,
                          zIndex: 15,
                        }}
                      />
                    )}

                    <TransparentTooltip
                      key={markerKey}
                      placement={dynamicPlacement}
                      // placement="top"
                      open={isActive}
                      disableHoverListener
                      disableFocusListener
                      disableTouchListener
                      // --- [여기 수정] 위치에 따른 offset 오버라이드 ---
                      slotProps={{
                        popper: {
                          modifiers: [
                            {
                              name: 'offset',
                              options: {
                                // PC와 모바일 동일한 간격 적용
                                offset: [0, isTopArea ? -25 : 0],
                              },
                            },
                            ...(isMobile
                              ? [
                                  {
                                    name: 'flip',
                                    options: {
                                      boundary: 'viewport',
                                      padding: 8,
                                    },
                                  },
                                  {
                                    name: 'preventOverflow',
                                    options: {
                                      boundary: 'viewport',
                                      altAxis: true,
                                      tether: true,
                                      padding: 8,
                                    },
                                  },
                                ]
                              : []),
                          ],
                        },
                      }}
                      title={
                        <div className="map_pin"/* style={{ width: "350px", height: "150px", padding: "20px", background: "unset" }}*/>
                          <div className="company-marker-tooltip" style={{ width: "300px", padding: "12px 16px", borderRadius: "14px", color: "#fff", fontSize: "13px", display: "flex", flexDirection: "column", border: "1px solid rgba(0, 0, 0, 0.25)", gap: "10px", transform: "translateY(10px)" }}>
                            <div style={{ borderRadius: "16px", width: "100%", background: statusColor?.bg2, color: statusColor?.color, padding: "6px", textAlign: "center", fontWeight: "bold", fontSize: "13px", boxShadow: "0 2px 4px rgba(0,0,0,0.2)" }}>
                              {m.equipment ? `${m.equipment.name} (${m.equipment.equipmentNo})` : t('message.common.text.Unmapped')}
                            </div>

                            <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "4px" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <span style={{ color: "#fff", fontSize: "12px" }}>{t("message.common.subtitle.equipment.level3")}</span>
                                <span style={{ fontWeight: "500", fontSize: "11px" }}>{m.equipment?.equipmentCtgr?.name ?? "-"}</span>
                              </div>

                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <span style={{ color: "#fff", fontSize: "12px" }}>{t("message.common.subtitle.status")}</span>
                                <span style={{ background: statusColor?.bg2, color: statusColor?.color, padding: "2px 10px", borderRadius: "12px", fontSize: "11px", fontWeight: "bold" }}>
                                  {getEnumValueByEnumNameAndCode("EquipmentFaultStatus", m.equipment?.status)?.[0]?.title ?? "-"}
                                </span>
                              </div>

                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <span style={{ color: "#fff", fontSize: "12px" }}>{t("message.common.subtitle.operation.info")}</span>
                                <span style={{ background: m.equipment?.latestEquipmentOp?.opStatus?.toUpperCase() === "ACTIVE" ? "#2672f7" : "#777", padding: "2px 10px", borderRadius: "12px", fontSize: "11px" }}>
                                  {m.equipment?.latestEquipmentOp?.opStatus?.toUpperCase() === "ACTIVE" ? t('message.common.text.active') : t('message.common.text.stop')}
                                </span>
                              </div>

                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <span style={{ color: "#fff", fontSize: "12px" }}>{t('message.common.subtitle.iso')} {t("message.common.subtitle.grade")}</span>
                                <span style={{ background: isoColor, color: isoStatus?.fontColor, width: "20px", height: "20px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", fontWeight: "bold" }}>
                                  {isoCode}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      }
                    >
                      {/* 2. 마커 본체: 선택되었을 때 스케일(1.35배) 및 Drop-Shadow 효과 강력 적용 */}
                      <div
                        id={markerKey}
                        className="marker-element"
                        style={{
                          position: "absolute",
                          top: `${posY}%`,
                          left: `${posX}%`,
                          // transform: "translate(-50%, -50%)",
                          transform: isActive ? "translate(-50%, -50%) scale(1.35)" : "translate(-50%, -50%) scale(1)",
                          transition: "transform 0.25s ease, filter 0.25s ease",
                          zIndex: isActive ? 30 : 10,
                          cursor: "pointer",
                          filter: isActive 
                            ? `drop-shadow(0px 0px 10px ${mainStatusColor}) drop-shadow(0px 0px 20px ${mainStatusColor})` 
                            : "drop-shadow(0px 2px 4px rgba(0,0,0,0.5))",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkerClick(m, index);
                        }}
                      >
                        <img
                          src={markerColorByStatus[m.equipment?.status || "unmapped"]}
                          alt="marker"
                          className="company-equipment-marker"
                          style={{ width: 32, height: 32, display: "block" }}
                        />
                      </div>
                    </TransparentTooltip>
                  </React.Fragment>
                  );
                })}

              </div>

            </div>

            {zoneLocationMap?.length > 1 && (
              <div style={{ display: "flex", gap: "6px", padding: "0 4px", flexShrink: 0 }}>
                {zoneLocationMap?.map((map, bi) => (
                  <Button
                    key={map.id}
                    variant={map.id == mapsId ? "contained" : "outlined"}
                    color="primary"
                    size="small"
                    onClick={() => {
                      navigate(`/dashboard/sub?zId=${zoneId}&mId=${map.id}`);
                    }}
                  >
                    {t('message.common.subtitle.equipmentPart.image')}{bi + 1}
                  </Button>
                ))}
              </div>
            )}

          </div>
        </div>

        {/* 우측 사이드 카드 (selectedId 변동 시 완벽 연동) */}
        <div className="right-aside-card">
          <RightAsideCard equipments={equipments} latestLogs={latestLogs} selectedId={selectedId} />
        </div>

        {/* 최상위 컨테이너 전체를 덮는 SVG 연결선 (activeMarker 변동 시 완벽 연동) */}
        <ImageMarkerConnectLine activeMarker={activeMarker} containerRef={containerRef} arrowColor={activeArrowColor} />
      </div>
    </React.Fragment>
  );
}

export default CompanyImageViewer;